import { Box } from '@mui/material'
import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import CopyableKeyDisplay from 'src/components/CopyableKeyDisplay/CopyableKeyDisplay'
import Trns from 'src/localization/Trns'
import { selectAPIKeyCreatedAPIKeyFromState } from 'src/redux/selectors/app.selectors'
import { css } from 'styled-components'

interface APIKeyCreatedFormProps {
  closeAPIKeyCreatedForm: () => void
}

const APIKeyCreatedForm: React.FC<APIKeyCreatedFormProps> = ({
  closeAPIKeyCreatedForm,
}) => {
  const apiKey = useSelector(selectAPIKeyCreatedAPIKeyFromState)
  const dispatch = useDispatch()
  useEffect(() => {
    if (apiKey === undefined) {
      closeAPIKeyCreatedForm()
      dispatch(
        createSetIsToastOpen(true, {
          failureColorScheme: true,
          hideImage: true,
          toastMessage: <Trns source={'API Key not found'} />,
        }),
      )
    }
  }, [apiKey])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )}
        `}
      >
        <Trns source={'API Key Generated'} />
      </span>
      <Box
        css={css`
          margin-top: 16px;
          margin-bottom: 32px;
          width: 100%;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          <Trns
            source={
              'Please save this ${secretKey} somewhere safe and accessible. For security reasons, ${viewAgain} through your Extend account. If you lose this security key, you will need to generate a new one.'
            }
            expressions={{
              secretKey: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  <Trns source={'secret key'} />
                </span>
              ),
              viewAgain: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  <Trns source={'you will not be able to view it again'} />
                </span>
              ),
            }}
          />
        </span>
      </Box>
      <Box>
        <span
          css={css`
            ${getFontCss(
              16,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          <Trns source={'API Key ID'} />
        </span>
      </Box>
      <Box
        css={css`
          margin-top: 16px;
          margin-bottom: 32px;
          width: 100%;
        `}
      >
        <CopyableKeyDisplay text={apiKey?.id ?? ''} copyEnabled={true} />
      </Box>
      <Box>
        <span
          css={css`
            ${getFontCss(
              16,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          <Trns source={'API Secret Key'} />
        </span>
      </Box>
      <Box
        css={css`
          margin-top: 16px;
          margin-bottom: 32px;
          width: 100%;
        `}
      >
        <CopyableKeyDisplay text={apiKey?.secret ?? ''} copyEnabled={true} />
      </Box>
      <Box
        css={css`
          width: 100%;
        `}
      >
        <Box>
          <span
            css={css`
              ${getFontCss(
                16,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            <Trns source={'Expiration Date (MM/DD/YY)'} />
          </span>
        </Box>
        <Box
          css={css`
            margin-top: 16px;
            width: 50%;
          `}
        >
          <CopyableKeyDisplay text={apiKey?.expiry ?? ''} copyEnabled={false} />
        </Box>
      </Box>
    </div>
  )
}

export default APIKeyCreatedForm
